/**
 * Brand Setup — wizard draft persistence (spec §2.6).
 *
 * The wizard writes its whole state to `localStorage` on every reducer
 * change (debounced 300 ms by the caller) so a closed tab, a refresh, or a
 * "come back tomorrow" doesn't lose a half-filled brand. This module is the
 * PURE half of that: it decides what is safe to persist and how to bring it
 * back, with `Storage` narrowed to just the methods used (`Pick<Storage,
 * "getItem">` etc.) so tests exercise it with a plain in-memory fake — no
 * DOM, no jsdom.
 *
 * Two things never touch `localStorage`:
 *   - `logoDataUrl` and `fonts.uploaded` — binary payloads that blow the
 *     ~5 MB quota fast. A wordmark logo is REDRAWABLE from `wordmark`
 *     (kept in full), so restoring it is silent; an uploaded logo or font is
 *     simply gone and has to be re-supplied. `needsLogo` / `needsFont` on
 *     the restore result are how the caller knows to ask again — and for a
 *     font, `restoreDraft` also resets any role (`fonts.display`/`.body`)
 *     that pointed at the stripped upload back to the language's seed face,
 *     so the state it hands back is never one `payload.ts` would ship an
 *     empty font file from (`fonts.uploaded` with a hollow `dataUrl`).
 *   - the study's `patch` / `steps` / `sources` / `failures` / nested
 *     `study` — everything the wizard has ALREADY folded into plain state
 *     fields (`colors`, `fonts`, `designPatch`, `chips`, …) or has no use
 *     for after the read finished. Only `id` / `chips` / `extras` survive,
 *     which is exactly what `state.study?.chips` (the chip rows) still reads.
 */
import type { BrandStudyRecord } from "../../api";
import { defaultFontsForLanguage, initialBrandSetupState, type BrandSetupState } from "./state";
import { visibleSteps, stepNumber, type StepId } from "./steps";

export const DRAFT_KEY = "ql-brand-setup-draft-v1";

/** The three fields of a finished study the wizard still reads once it is
 *  folded into state (`state.study?.chips`, `.extras`, `.id`) — everything
 *  else on `BrandStudyRecord` is either re-derivable or a one-shot detail of
 *  the read itself, so it never reaches `localStorage`. */
export type SerializedStudy = Pick<BrandStudyRecord, "id" | "chips" | "extras">;

/** `BrandSetupState`, minus every byte a browser quota would choke on. */
export type SerializedDraft = Omit<BrandSetupState, "logoDataUrl" | "fonts" | "study"> & {
  fonts: Pick<BrandSetupState["fonts"], "display" | "body"> & {
    /** `family` names WHICH font was uploaded (so a restored Font step can
     *  still say "you uploaded X" and offer to redraw); `dataUrl` — the
     *  actual bytes — never rides along. */
    uploaded?: { family: string };
  };
  study?: SerializedStudy;
};

export interface DraftEnvelope {
  v: 1;
  savedAt: number;
  furthest: StepId;
  state: SerializedDraft;
}

/**
 * State → envelope. Strips `logoDataUrl` and the whole `fonts.uploaded`
 * (its only reason to exist is to carry a data URL to Create — useless
 * without it) and reduces `study` to `{ id, chips, extras }`. Everything
 * else — name, slug, language, sources, colors, fonts.display/body,
 * designPatch, chips, wordmark, keptAll, returnTo, accepted/edited — rides
 * along untouched, which is what keeps a restored draft indistinguishable
 * from the live state it was taken from (short of the two stripped fields).
 */
export function serializeDraft(state: BrandSetupState, furthest: StepId): DraftEnvelope {
  const { logoDataUrl: _logoDataUrl, fonts, study, ...rest } = state;
  return {
    v: 1,
    savedAt: Date.now(),
    furthest,
    state: {
      ...rest,
      fonts: {
        display: fonts.display,
        body: fonts.body,
        ...(fonts.uploaded ? { uploaded: { family: fonts.uploaded.family } } : {}),
      },
      ...(study ? { study: { id: study.id, chips: study.chips, extras: study.extras } } : {}),
    },
  };
}

/** Every step id the wizard can be on, on either lane — derived from
 *  `visibleSteps` (the `"auto"` lane shows the union) rather than duplicated
 *  here, so a step added in `steps.ts` is validated automatically. */
const ALL_STEP_IDS: ReadonlySet<StepId> = new Set(visibleSteps({ mode: "auto" }).map((d) => d.id));

function isStepId(v: unknown): v is StepId {
  return typeof v === "string" && ALL_STEP_IDS.has(v as StepId);
}

/** Enough structural validation to reject garbage and a version bump — not
 *  exhaustive field-by-field checking, which would just duplicate the type. */
function isDraftEnvelope(env: unknown): env is DraftEnvelope {
  if (!env || typeof env !== "object") return false;
  const e = env as Record<string, unknown>;
  if (e.v !== 1) return false;
  if (typeof e.savedAt !== "number") return false;
  if (!isStepId(e.furthest)) return false;
  if (!e.state || typeof e.state !== "object") return false;
  const s = e.state as Record<string, unknown>;
  if (!isStepId(s.step)) return false;
  if (typeof s.brandName !== "string") return false;
  if (s.mode !== null && s.mode !== "auto" && s.mode !== "manual") return false;
  if (!s.fonts || typeof s.fonts !== "object") return false;
  return true;
}

/** Rebuild a minimal-but-valid `BrandStudyRecord` from the three fields the
 *  draft kept, so `state.study?.chips` / `.extras` / `.id` keep working. The
 *  fields a real record carries beyond those (timestamps, status, steps,
 *  sources, failures) are stubbed — nothing downstream of a restored draft
 *  reads them; only the three that made it into `SerializedStudy` do. */
function rebuildStudy(s: SerializedStudy, savedAt: number): BrandStudyRecord {
  const ts = new Date(savedAt).toISOString();
  return {
    id: s.id,
    createdAt: ts,
    updatedAt: ts,
    status: "done",
    steps: [],
    sources: [],
    failures: [],
    chips: s.chips,
    extras: s.extras,
  };
}

/** Pull `furthest` back to (at most) `"logo"` when the stored logo was an
 *  upload — its bytes are gone, so a "Continue setup" that landed past it
 *  would strand the operator on a later screen with no way back to re-supply
 *  it. Already at or before `"logo"` (or off this lane's walk entirely, which
 *  should not happen) is left alone. */
function clampFurthestForLogo(furthest: StepId, mode: BrandSetupState["mode"]): StepId {
  const order = visibleSteps({ mode }).map((d) => d.id);
  const logoIdx = order.indexOf("logo");
  if (logoIdx < 0) return furthest;
  const furthestIdx = order.indexOf(furthest);
  if (furthestIdx >= 0 && furthestIdx <= logoIdx) return furthest;
  return "logo";
}

/**
 * Which of the two font roles pointed at the now-stripped upload, reset to
 * the language's seed face (`defaultFontsForLanguage`). A role the operator
 * pointed at a LIBRARY face is untouched — only a role that WAS the upload
 * moves, so a draft with an uploaded display face and a library body face
 * restores with body exactly as it was.
 */
function resolveFontRole(raw: unknown, fallback: string, needsFont: boolean, uploadedFamily: string | undefined, seedValue: string): string {
  const value = typeof raw === "string" ? raw : fallback;
  return needsFont && value === uploadedFamily ? seedValue : value;
}

/**
 * Envelope → live state. Null on anything that doesn't look like a v1 draft
 * (garbage, a future version, a hand-edited/corrupt blob) — the caller's job
 * is then to fall back to `initialBrandSetupState()`, never to half-apply a
 * broken draft.
 *
 * The result is layered `{ ...initialBrandSetupState(), ...rest, … }`: any
 * field genuinely absent from `rest` (an older or hand-truncated v1 envelope
 * — `isDraftEnvelope` checks the shape, not every field) falls back to the
 * wizard's own default instead of surfacing as `undefined`, so a partial
 * draft is a HANDICAPPED restore, never a broken one.
 *
 * `needsLogo` is true only for `logoKind === "upload"` (the caller re-asks
 * for the file); a `"wordmark"` logo comes back with `logoDataUrl: ""` too,
 * but keeps `wordmark` so the caller redraws it from those parameters — no
 * re-ask needed. `needsFont` is true whenever the draft carried an upload —
 * `fonts.uploaded` is ALWAYS dropped on restore (its `dataUrl` never
 * survived serialization, and a `{dataUrl: "", family}` stand-in is exactly
 * the shape `payload.ts` would ship to Create as an empty font file — see
 * the module doc comment), and any role (`display` / `body`) that pointed at
 * it resets to `defaultFontsForLanguage(state.language)` so the state this
 * returns is always renderable, never one waiting on bytes that are gone —
 * and when `displaySame` is true, `display` is re-mirrored onto `body`
 * afterwards, holding `state.ts`'s `mirrorDisplayFont` invariant even though
 * the two roles were just reset independently.
 */
export function restoreDraft(
  env: unknown,
): { state: BrandSetupState; furthest: StepId; needsLogo: boolean; needsFont: boolean } | null {
  if (!isDraftEnvelope(env)) return null;
  const { state: s, savedAt, furthest: storedFurthest } = env;
  const needsLogo = s.logoKind === "upload";

  const { fonts, study, ...rest } = s;
  const base = initialBrandSetupState();
  const needsFont = !!fonts.uploaded;
  const uploadedFamily = fonts.uploaded?.family;
  const seed = defaultFontsForLanguage(s.language ?? base.language);

  const resolvedFonts = {
    display: resolveFontRole(fonts.display, base.fonts.display, needsFont, uploadedFamily, seed.display),
    body: resolveFontRole(fonts.body, base.fonts.body, needsFont, uploadedFamily, seed.body),
  };
  // Hold `state.ts`'s `mirrorDisplayFont` invariant (`displaySame` ⇒
  // `display === body`) ourselves: that mirror runs on every REDUCER action,
  // but a restore builds state directly, bypassing the reducer, and the two
  // roles above were just reset INDEPENDENTLY (each to its own seed face) —
  // resetting a displaySame draft would otherwise land it split for the
  // first render.
  if (rest.displaySame) resolvedFonts.display = resolvedFonts.body;

  const state: BrandSetupState = {
    ...base,
    ...rest,
    fonts: resolvedFonts,
    logoDataUrl: "",
    study: study ? rebuildStudy(study, savedAt) : undefined,
  };

  const furthest = needsLogo ? clampFurthestForLogo(storedFurthest, s.mode) : storedFurthest;

  return { state, furthest, needsLogo, needsFont };
}

/** Read + parse + restore in one call. Any failure — missing key, invalid
 *  JSON, a storage accessor that throws (private-mode Safari, …), a shape
 *  `restoreDraft` rejects — comes back as `null`, never a throw. */
export function loadDraft(storage: Pick<Storage, "getItem">): ReturnType<typeof restoreDraft> {
  let raw: string | null;
  try {
    raw = storage.getItem(DRAFT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  return restoreDraft(parsed);
}

function isQuotaExceeded(err: unknown): boolean {
  if (typeof err !== "object" || err === null) return false;
  const e = err as { name?: unknown; code?: unknown };
  return e.name === "QuotaExceededError" || e.code === 22;
}

/**
 * Write the envelope. `"quota"` is a distinct outcome (not a throw) because
 * the caller has a real fallback for it — a quota-failure sentence in the
 * UI, per spec §2.6 — while any OTHER storage failure is unexpected and
 * should propagate.
 */
export function saveDraft(storage: Pick<Storage, "setItem">, env: DraftEnvelope): "ok" | "quota" {
  try {
    storage.setItem(DRAFT_KEY, JSON.stringify(env));
    return "ok";
  } catch (err) {
    if (isQuotaExceeded(err)) return "quota";
    throw err;
  }
}

export function clearDraft(storage: Pick<Storage, "removeItem">): void {
  storage.removeItem(DRAFT_KEY);
}

/** The next COUNTED step at or after `from` on this lane, or null past the
 *  end of the walk (e.g. `from` is `"done"`). */
function nextCountedStepId(mode: BrandSetupState["mode"], from: StepId): StepId | null {
  const order = visibleSteps({ mode });
  const idx = order.findIndex((d) => d.id === from);
  if (idx < 0) return null;
  return order.slice(idx).find((d) => d.counted)?.id ?? null;
}

/**
 * A draft exists → what "Continue setup" should say. `furthest` can itself be
 * an uncounted screen (`welcome`, `reading`) — there is no "Step n of N" for
 * those, so this falls forward to the next counted step and reports THAT
 * one's number (and id), rather than a label with no number in it.
 */
export function hasDraft(
  storage: Pick<Storage, "getItem">,
): { step: StepId; n: number; total: number } | null {
  const restored = loadDraft(storage);
  if (!restored) return null;
  const id = nextCountedStepId(restored.state.mode, restored.furthest) ?? restored.furthest;
  const numbered = stepNumber({ mode: restored.state.mode }, id);
  if (!numbered) return null;
  return { step: id, n: numbered.n, total: numbered.total };
}
